import { formatCurrency } from './formatting';
import { computeBalanceCostBasis } from './calculations';

export function validateTransaction(form) {
  const errs = {};
  if (!form.date) errs.date = 'Date required';
  if (form.type !== 'savings' && !form.category) errs.category = 'Category required';
  if (!form.description || !form.description.trim()) errs.description = 'Description required';
  if (!form.amount || parseFloat(form.amount) <= 0) errs.amount = 'Valid amount required';
  if (form.type === 'savings' && !form.bucket) errs.bucket = 'Select a savings bucket';
  return errs;
}

/**
 * Build the transaction object + label, applying foreign-currency rate logic.
 * Pure function — does not dispatch.
 */
export function buildTransaction(form, ctx) {
  const { settings, currencyBalances, categories } = ctx;
  const mainCurrency = settings.currency || 'ILS';
  const isForeign = form.currency && form.currency !== mainCurrency;
  const foreignAmt = parseFloat(form.amount) || 0;

  let rate;
  if (isForeign) {
    const matchBal = currencyBalances.find(b => b.currency === form.currency);
    const { totalForeign: balTotal, avgRate } = matchBal
      ? computeBalanceCostBasis(matchBal)
      : { totalForeign: 0, avgRate: 0 };
    if (form.fromBalance && avgRate === 0) {
      throw new Error('No foreign balance available — add a purchase lot first');
    }
    rate = form.fromBalance ? avgRate : (parseFloat(form.exchangeRate) || 1);
  } else {
    rate = 1;
  }

  const nisAmt = isForeign ? parseFloat((foreignAmt * rate).toFixed(2)) : foreignAmt;

  // Parse comma-separated tags string into a deduplicated array
  const tags = form.tags
    ? [...new Set(form.tags.split(',').map(t => t.trim()).filter(Boolean))]
    : [];

  const txn = {
    date: form.date,
    type: form.type,
    category: form.type === 'savings' ? '' : form.category,
    description: form.description.trim(),
    amount: parseFloat(nisAmt.toFixed(2)),
    bucket: form.bucket || null,
    currency: form.currency || mainCurrency,
    foreignAmount: isForeign ? foreignAmt : null,
    exchangeRate: isForeign ? rate : null,
    fromBalance: isForeign && form.fromBalance ? true : undefined,
    // Keep the balance relationship on the transaction so its matching spend
    // can follow Tracker updates and deletes.
    currencyBalanceId: isForeign && form.fromBalance
      ? currencyBalances.find(b => b.currency === form.currency)?.id || null
      : null,
    tags: tags.length > 0 ? tags : undefined,
  };

  const fmt = v => formatCurrency(v, settings);
  const cat = categories.find(c => c.id === form.category);
  const label = isForeign
    ? `${foreignAmt} ${form.currency} = ${fmt(txn.amount)}`
    : `${cat ? cat.name : ''} ${fmt(txn.amount)}`;

  return { txn, label, isForeign, foreignAmt, nisAmt, rate };
}

/**
 * Validate, build, dispatch, toast.
 * Returns true on success, false on validation failure (errors are surfaced via setErrors).
 */
export function submitTransaction(form, ctx) {
  const { setErrors, showToast, addTransaction, updateTransaction, editId } = ctx;

  const errs = validateTransaction(form);
  if (Object.keys(errs).length) {
    setErrors?.(errs);
    showToast?.('Please fix the errors', 'error');
    return false;
  }

  let built;
  try {
    built = buildTransaction(form, ctx);
  } catch (err) {
    setErrors?.({ amount: err.message });
    showToast?.(err.message, 'error');
    return false;
  }
  const { txn, label } = built;

  if (editId) {
    updateTransaction(editId, txn);
    showToast?.('Transaction updated');
  } else {
    addTransaction(txn);
    const typeLabel = form.type === 'income' ? 'Income' : form.type === 'savings' ? 'Savings' : 'Expense';
    showToast?.(`${typeLabel} logged: ${label}`);
  }
  setErrors?.({});
  return true;
}
